// Efeitos sonoros do LibriTui: cantos de tūī tratados, curtos e baixos (arquivos em public/sounds).
export type SoundName = 'tap' | 'success' | 'error' | 'partial' | 'notify' | 'notification' | 'mission' | 'logo';

// Sons desligados até a escolha dos novos efeitos (opções em branding/sounds/opcoes). true religa tudo.
export const SOUNDS_ENABLED = true;

export interface SoundPrefs {
  push: boolean;
  effects: boolean;
  buttons: boolean;
  volume: number; // 0 a 1
}

const PREFS_KEY = 'libritui_sound_prefs';
const DEFAULTS: SoundPrefs = { push: true, effects: true, buttons: false, volume: 0.7 };
const listeners = new Set<(p: SoundPrefs) => void>();
const buffers = new Map<SoundName, Promise<AudioBuffer | null>>();
const lastPlayed = new Map<SoundName, number>();
let ctx: AudioContext | null = null;

const readPrefs = (): SoundPrefs => {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }; } catch { return { ...DEFAULTS }; }
};

const getCtx = (): AudioContext | null => {
  if (ctx) return ctx;
  const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  return ctx;
};

const load = (name: SoundName): Promise<AudioBuffer | null> => {
  if (!buffers.has(name)) {
    buffers.set(name, (async () => {
      const c = getCtx();
      if (!c) return null;
      try {
        const res = await fetch(`/sounds/${name}.mp3`);
        return await c.decodeAudioData(await res.arrayBuffer());
      } catch {
        buffers.delete(name);
        return null;
      }
    })());
  }
  return buffers.get(name)!;
};

export const soundService = {
  getPrefs: readPrefs,

  setPrefs(patch: Partial<SoundPrefs>): SoundPrefs {
    const next = { ...readPrefs(), ...patch };
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch {}
    listeners.forEach(fn => fn(next));
    return next;
  },

  subscribe(fn: (p: SoundPrefs) => void) {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },

  /** Toca um efeito respeitando as preferências; `force` é usado no botão "Ouvir" das configurações. */
  async play(name: SoundName, force = false) {
    if (!SOUNDS_ENABLED) return;
    const prefs = readPrefs();
    if (!force && (name === 'tap' ? !prefs.buttons : !prefs.effects)) return;
    const now = Date.now();
    if (now - (lastPlayed.get(name) || 0) < 150) return;
    lastPlayed.set(name, now);
    const c = getCtx();
    if (!c) return;
    if (c.state === 'suspended') {
      try { await c.resume(); } catch { return; }
    }
    const buf = await load(name);
    if (!buf) return;
    const src = c.createBufferSource();
    src.buffer = buf;
    const gain = c.createGain();
    gain.gain.value = Math.max(0, Math.min(1, prefs.volume));
    src.connect(gain);
    gain.connect(c.destination);
    src.start();
  },

  /** Toque discreto em botões (desligado por padrão). Ligado uma vez na abertura do app. */
  init() {
    if ((window as any).__ltSoundInit) return;
    (window as any).__ltSoundInit = true;
    document.addEventListener('pointerdown', (e) => {
      if (!readPrefs().buttons) return;
      const el = (e.target as HTMLElement | null)?.closest?.('button, [role="button"], a[href]') as HTMLButtonElement | null;
      if (el && !el.disabled) soundService.play('tap');
    }, { capture: true, passive: true });
  },
};
